import React, { Dispatch, SetStateAction, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IconLinkedCampaign } from '../../../../../shared/Icons';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { DashboardLinkedCampaignList } from './DashboardLinkedCampaignList';
import { ILinkedCampaigns } from '../../../../../model/campaign/Campaign';
import { DashboardLinkedListItem } from './DashboardLinkedListItem';
import { CampaignCreateModal } from '../../briefing/modal/CampaignCreateModal';
import { hasAccessRights } from '../../../../../shared/functions/Functions';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import InfiniteScroll from 'react-infinite-scroll-component';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { DashboardLinkedCampaignCardSkeleton, DashboardLinkedRowsSkeleton } from './LongTermDashboardSkeletons';

interface IDashboardLinkedCampaignCardProps {
    campaigns: ILinkedCampaigns[];
    setFilterParams: Dispatch<SetStateAction<{ page: number; perPage: number }>>;
    filterParams: { page: number; perPage: number };
    campaignCount: number;
    isLoading?: boolean;
}

export function DashboardLinkedCampaignCard({
    campaigns,
    setFilterParams,
    filterParams,
    campaignCount,
    isLoading = false,
}: IDashboardLinkedCampaignCardProps) {
    const { t } = useTranslation();
    const [showBriefingModal, setShowBriefingModal] = useState(false);
    const { id } = useParams<keyof IParams>();
    const canCreate = hasAccessRights(AccessRightEnum.CAN_VIEW_CONTRACTS);

    const handlePerPage = (numberPerPage: number) => {
        setFilterParams(prev => ({ ...prev, perPage: numberPerPage }));
    };

    if (isLoading) {
        return <DashboardLinkedCampaignCardSkeleton withButton={canCreate} />;
    }

    return (
        <>
            <CampaignCreateModal
                show={showBriefingModal}
                closeModal={() => setShowBriefingModal(false)}
                longTermCampaignId={id ? +id : undefined}
            />
            <CardLayout classCard="mb-3">
                <div className="d-flex justify-content-between">
                    <div className="d-flex align-items-center textHover">
                        <IconLinkedCampaign width="20" height="20" color={MUTED_TEXT_COLOR} classes={' mr-2'} />
                        <h6 className="mb-0 ">{t('brand.campaign.dashboard.linked-campaigns.linked')}</h6>
                    </div>
                    {canCreate && (
                        <Button size="sm" onClick={() => setShowBriefingModal(true)}>
                            {t('brand.create_new_campaign')}
                        </Button>
                    )}
                </div>
                <div className="my-3">
                    <div id="scrollableDiv" style={{ maxHeight: 330, overflow: 'auto' }}>
                        {campaigns.length > 0 ? (
                            <InfiniteScroll
                                dataLength={campaigns?.length}
                                next={() => handlePerPage(filterParams?.perPage + 5)}
                                endMessage={
                                    <p
                                        className={`text-center ${
                                            campaigns?.length !== campaignCount || filterParams?.perPage <= 10
                                                ? 'd-none'
                                                : ''
                                        }`}
                                    >
                                        <b>{t('general.seen_all_campaigns')}</b>
                                    </p>
                                }
                                hasMore={campaigns?.length < campaignCount}
                                loader={<DashboardLinkedRowsSkeleton rows={2} />}
                                scrollableTarget="scrollableDiv"
                            >
                                <DashboardLinkedCampaignList nbCampaigns={campaignCount}>
                                    <>
                                        {campaigns?.map(item => (
                                            <DashboardLinkedListItem key={item.id} campaign={item} />
                                        ))}
                                    </>
                                </DashboardLinkedCampaignList>
                            </InfiniteScroll>
                        ) : (
                            <EmptyState
                                iconPath="/icons/img-linked-campaigns.svg"
                                desc={t('brand.campaign.dashboard.linked-campaigns.empty_state')}
                                imgClass="w-80px"
                            />
                        )}
                    </div>
                </div>
            </CardLayout>
        </>
    );
}
